import { toMajor as moneyToMajor42 } from '@/lib/region';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BusinessToolsShowcase } from '@/components/BusinessToolsShowcase';
import Seo from '@/components/Seo';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MarketingNav, MarketingFooter } from '@/components/MarketingFooter';
import { Check, Star, Gift } from 'lucide-react';

/**
 * /pricing — Phase 92 (site audit fixes D5.1 + D4.3 + FIT).
 *
 * Dedicated pricing page with its own title/meta (previously the route
 * fell through to the homepage). Carries the same live tiers as the
 * homepage #pricing section plus the transparency items the audit asked
 * for: the $0.25 per-transaction disclosure and the 1-on-1 onboarding
 * call (Pro only).
 */
export default function PricingPage() {
    const [tiers, setTiers] = useState([]);
    useEffect(() => {
        api.get('/public/pricing').then((r) => setTiers(r.data)).catch(() => {});
    }, []);

    return (
        <div className="min-h-screen bg-[#f4f7fb]">
            <Seo
                title="SplashPad Pricing — Starter $39.99/mo, Pro $79.99/mo"
                description="Flat monthly pricing for small swim schools and coaches. Starter $39.99/mo (40 students), Pro $79.99/mo (unlimited students, staff portal, payroll). No per-family fees. Free 30-day trial."
                canonical="https://splashpad.pro/pricing"
            />
            <MarketingNav />

            <section className="py-14 lg:py-20">
                <div className="mx-auto max-w-7xl px-4 sm:px-6">
                    <div className="mx-auto mb-12 max-w-3xl text-center">
                        <h1 className="font-display text-4xl font-bold text-[#0a1628] sm:text-5xl">Two plans. Pick what fits.</h1>
                        <p className="mt-3 text-[#1a3a5c]">
                            Start with a 30-day free trial on any plan.{' '}
                            <span className="font-semibold text-[#0a1628]">Pro includes a personal 1-on-1 onboarding call.</span>
                        </p>
                    </div>

                    <div className="mx-auto grid max-w-4xl gap-6 md:grid-cols-2">
                        {tiers.map((t) => {
                            const isPro = t.id === 'pro';
                            const dollars = (moneyToMajor42(t.price_cents, t?.currency || 'usd')).toFixed(2);
                            return (
                                <Card
                                    key={t.id}
                                    className={`sd-card relative flex flex-col p-6 ${isPro ? 'border-2 shadow-xl' : ''}`}
                                    style={isPro ? { borderColor: '#ffd700' } : {}}
                                    data-testid={`pricing-page-card-${t.id}`}
                                >
                                    {/* Phase 92 (D4.3) — recommended-tier labels. */}
                                    {isPro ? (
                                        <span className="mb-2 inline-flex w-fit items-center gap-1 rounded-full bg-[#ffd700] px-2.5 py-1 text-[11px] font-bold text-[#0a1628]" data-testid="pricing-page-most-popular">
                                            <Star className="h-3 w-3 fill-[#0a1628]" /> Most popular for swim schools with staff
                                        </span>
                                    ) : (
                                        <span className="mb-2 inline-flex w-fit items-center rounded-full bg-[#00d4ff]/15 px-2.5 py-1 text-[11px] font-semibold text-[#0a1628]" data-testid="pricing-page-starter-label">
                                            Best for solo coaches and brand-new schools
                                        </span>
                                    )}
                                    <h2 className="font-display text-2xl font-bold text-[#0a1628]">{t.name}</h2>
                                    {t.tagline && <p className="mt-1 text-sm text-[#1a3a5c]/80">{t.tagline}</p>}
                                    <div className="mt-4 flex items-baseline gap-1">
                                        <span className="text-5xl font-bold text-[#0a1628]">${dollars}</span>
                                        <span className="text-sm text-[#1a3a5c]/70">/month</span>
                                    </div>
                                    <p className="mt-2 text-xs text-[#1a3a5c]/80">
                                        {isPro
                                            ? 'SSU community members pay $39.99/mo (50% off) with their member code.'
                                            : 'FREE for verified SSU community members with their member code.'}
                                    </p>
                                    <ul className="mt-5 space-y-2.5">
                                        {/* Onboarding call is a Pro-only benefit — only on the Pro card. */}
                                        {isPro && (
                                            <li className="flex items-start gap-2 rounded-lg bg-[#00d4ff]/10 px-2 py-1 text-sm font-medium text-[#0a1628]" data-testid="pricing-page-pro-onboarding-call">
                                                <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#00d4ff]" />
                                                <span>Personal 1-on-1 onboarding call with a real swim coach</span>
                                            </li>
                                        )}
                                        {(t.features || []).map((f) => {
                                            const isCommunity = f.toLowerCase().includes('skool') || f.toLowerCase().includes('community');
                                            return (
                                                <li key={f} className={`flex items-start gap-2 text-sm ${isCommunity ? 'rounded-lg bg-[#ffd700]/15 px-2 py-1 font-medium text-[#0a1628]' : 'text-[#1a3a5c]'}`}>
                                                    {isCommunity
                                                        ? <Gift className="mt-0.5 h-4 w-4 shrink-0 text-[#ff6b35]" />
                                                        : <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#00d4ff]" />}
                                                    <span>{f}</span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                    <Link
                                        to={`/signup?role=instructor&plan=${t.id}`}
                                        className={`sd-focus mt-auto flex h-12 w-full items-center justify-center rounded-xl pt-0 text-sm font-semibold ${isPro ? 'sd-coral-cta' : 'sd-cta'}`}
                                        style={{ marginTop: '1.5rem' }}
                                        data-testid={`pricing-page-${t.id}-cta`}
                                    >
                                        {isPro ? 'Try Pro free for 30 days' : 'Start Free'}
                                    </Link>
                                </Card>
                            );
                        })}
                    </div>

                    {/* D4.3 / FIT — full fee transparency, the audit's exact wording. */}
                    <p className="mx-auto mt-8 max-w-2xl text-center text-sm font-medium text-[#0a1628]" data-testid="pricing-page-fee-disclosure">
                        Just $0.25 per successful transaction — that&apos;s it. No percentage cut, no per-family fee, no add-on fees.
                        Standard Stripe processing fees apply.
                    </p>
                    <p className="mx-auto mt-3 max-w-2xl text-center text-xs text-[#1a3a5c]/60">
                        Cancel any time. Upgrade or downgrade as you grow. Pro unlocks your custom landing page,
                        staff portal with payroll, location performance reports, and broadcast tools to scale your school.
                    </p>
                </div>
            </section>

            <BusinessToolsShowcase context="pricing" />
            <MarketingFooter />
        </div>
    );
}
